/**
 * LA TABLE — ce qu'on voit en ouvrant le livre quand on a un compte.
 *
 * Trois choses, dans cet ordre : ce qui est EN COURS (une partie interrompue se reprend en un
 * clic — le §12 range la reprise après trois semaines parmi les vrais risques du projet), ce qui
 * attend (l'atelier, les notifications), et ce qu'on peut commencer.
 */
import { api } from "../api/client.js";
import { useRessource } from "../api/chargement.js";
import type { HerosEnVue, NotificationEnVue, PartieResumee, Travail } from "../api/types.js";
import { Glyphe } from "../composants/Glyphe.js";
import { Filet, Parchemin } from "../composants/Parchemin.js";
import { ARCHETYPES, ETAPES_DATELIER, MODES, STATUTS_DE_PARTIE, dureeDite } from "../lexique/mots.js";
import { Lien, va } from "../routeur.js";
import { useSession } from "../session.js";
import { t } from "../lexique/langue.js";

function Notifications() {
  const { donnees, recharge } = useRessource<NotificationEnVue[]>(
    "/notifications",
    (charge: { notifications: NotificationEnVue[] }) => charge.notifications
  );
  const fraiches = (donnees ?? []).filter((n) => !n.lue);
  if (!fraiches.length) return null;

  return (
    <div className="pile pile--serree">
      {fraiches.map((n) => (
        <div key={n.id} className="message message--annonce" role="status">
          <span className="message__texte">
            {n.sujet === "adventure_ready"
              ? t("Votre aventure est prête. Elle vous attend au catalogue.")
              : n.sujet === "adventure_failed"
                ? t("Une aventure ne s'est pas écrite jusqu'au bout. Elle ne vous a rien coûté.")
                : t("Il y a du nouveau.")}
          </span>
          <button
            type="button"
            className="bouton bouton--discret"
            onClick={() => void api.poste(`/notifications/${n.id}/lue`).then(recharge)}
          >
            {t("Vu")}
          </button>
        </div>
      ))}
    </div>
  );
}

function Atelier() {
  const { donnees } = useRessource<Travail[]>(
    "/aventures/generations",
    (charge: { travaux: Travail[] }) => charge.travaux
  );
  const enCours = (donnees ?? []).filter((travail) => travail.statut === "en_attente" || travail.statut === "en_cours");
  if (!enCours.length) return null;

  return (
    <Parchemin variante="creux" titre={t("À l'atelier")}>
      {enCours.map((travail) => (
        <div key={travail.id} className="travail">
          <div className="rang rang--espace">
            <span>{travail.demande.titre ?? t("Aventure sans titre")}</span>
            <span className="capitales">{travail.statut === "en_attente" ? t("En file") : t("En fabrication")}</span>
          </div>
          <div className="travail__jauge" aria-hidden="true">
            <span style={{ width: `${Math.round(travail.progression * 100)}%` }} />
          </div>
          <p className="faible" style={{ margin: 0 }}>
            {travail.etape ? (ETAPES_DATELIER[travail.etape] ?? travail.etape) : t("Votre tour vient")}{" "}
            {t(
              "— comptez une demi-heure. Rien à surveiller : fermez l'onglet, allez faire autre chose, on vous préviendra ici."
            )}
          </p>
        </div>
      ))}
    </Parchemin>
  );
}

export function Table() {
  const { compte } = useSession();
  const parties = useRessource<PartieResumee[]>("/parties", (charge: { parties: PartieResumee[] }) => charge.parties);
  const heros = useRessource<HerosEnVue[]>("/heros", (charge: { heros: HerosEnVue[] }) => charge.heros);

  const enCours = (parties.donnees ?? []).filter((p) => p.statut === "active" || p.statut === "reprise");
  const vivants = (heros.donnees ?? []).filter((h) => h.status === "alive");

  return (
    <div className="page arrivee">
      <header className="entete-decran">
        <h1>{t("La table")}</h1>
        <p className="douce">{compte?.email}</p>
      </header>

      <Notifications />

      {enCours.length ? (
        <>
          <Filet>{t("Là où vous en étiez")}</Filet>
          <div className="grille grille--cartes" style={{ marginTop: "var(--e5)" }}>
            {enCours.map((p) => (
              <Parchemin key={p.id} variante="leve">
                <h2 style={{ fontSize: "var(--pas-3)" }}>{p.aventure.titre}</h2>
                <p className="douce" style={{ marginTop: "var(--e2)" }}>
                  {p.heros.nom} — {MODES[p.mode]?.nom ?? p.mode}
                </p>
                <p className="rang rang--serre faible">
                  <Glyphe nom="horloge" taille={15} /> {t("{duree} de jeu", { duree: dureeDite(p.heure) })}
                  <span>·</span>
                  {STATUTS_DE_PARTIE[p.statut] ?? p.statut}
                </p>
                <button type="button" className="bouton bouton--grand" onClick={() => va(`/partie/${p.id}`)}>
                  {p.statut === "reprise" ? t("Désigner la cicatrice") : t("Reprendre")}
                </button>
              </Parchemin>
            ))}
          </div>
        </>
      ) : null}

      <div style={{ marginTop: "var(--e7)" }}>
        <Atelier />
      </div>

      <div style={{ marginTop: "var(--e7)" }}>
        <Filet>{t("Vos héros")}</Filet>
      </div>

      {vivants.length ? (
        <div className="grille grille--cartes" style={{ marginTop: "var(--e5)" }}>
          {vivants.map((h) => (
            <Lien key={h.id} vers={`/heros/${h.id}`} className="carte-heros">
              <span className="carte-heros__nom">{h.nom}</span>
              <span className="capitales">{ARCHETYPES[h.archetype]?.nom ?? h.archetype}</span>
              <span className="rang rang--serre faible">
                <Glyphe nom="vie" taille={14} couleur="var(--sang)" />
                <span className="nombre">
                  {h.pv} / {h.pvMax}
                </span>
                <span>·</span>
                <span className="nombre" title={t("Indice de puissance de l'équipement — c'est ce qu'une aventure regarde à la porte.")}>
                  {t("équipement")} {h.ipe}
                </span>
                <span>·</span>
                {h.palier}
              </span>
            </Lien>
          ))}
        </div>
      ) : (
        <Parchemin variante="pose" className="vide">
          <p>
            {t("Personne ne partira à votre place. Il vous faut un héros avant d'ouvrir une aventure — un seul à la fois, tant que le compte est gratuit.")}
          </p>
          <Lien vers="/heros/nouveau" className="bouton bouton--grand">
            {t("En créer un")}
          </Lien>
        </Parchemin>
      )}

      <div className="rang" style={{ marginTop: "var(--e6)" }}>
        <Lien vers="/heros/nouveau" className="bouton">
          {t("Nouveau héros")}
        </Lien>
        <Lien vers="/aventures" className="bouton">
          {t("Le catalogue")}
        </Lien>
      </div>
    </div>
  );
}
